import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { remarkUcs, ATRIBUTO_UC } from '../../src/components/comentarios/ucNoTexto.ts';
import { UcCitadaBotao, useUcCitada } from '../../src/components/comentarios/UcCitada';

interface TaskDescriptionMarkdownProps {
    value: string;
    /**
     * 'compact' → caixas de preview pequenas (editor lado a lado, min-h ~120-150px).
     * 'full' → tela de detalhe, com mais espaço pra respirar.
     */
    variant?: 'compact' | 'full';
}

/**
 * Renderiza descrição/comentário em markdown com estilos explícitos por elemento.
 *
 * O reset do Tailwind (preflight) zera font-size/font-weight de h1-h6 e a marcação de
 * <ul>/<ol> — sem esse `components` a descrição "parseia" o markdown mas fica visualmente
 * idêntica a texto corrido (sem negrito, sem títulos maiores, sem bullets). Centralizado
 * aqui pra não repetir (e esquecer) esse mapeamento em cada tela nova que exibe descrição.
 */
const TaskDescriptionMarkdown: React.FC<TaskDescriptionMarkdownProps> = ({ value, variant = 'full' }) => {
    const compact = variant === 'compact';
    // #6587: UC citada clicável só onde a tela liga o UcCitadaContext.
    const ucs = useUcCitada()?.conhecidos;

    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks, ...(ucs?.size ? [remarkUcs(ucs) as never] : [])]}
            components={{
                h1: ({ children }) => (
                    <h1 className={`font-bold text-gray-800 dark:text-white border-b border-gray-200 dark:border-gray-700 first:mt-0 ${compact ? 'text-base pb-1 mb-2 mt-3' : 'text-lg pb-2 mb-3 mt-4'}`}>{children}</h1>
                ),
                h2: ({ children }) => (
                    <h2 className={`font-bold text-gray-800 dark:text-white border-b border-gray-200 dark:border-gray-700 first:mt-0 ${compact ? 'text-sm pb-1 mb-2 mt-3' : 'text-base pb-2 mb-3 mt-5'}`}>{children}</h2>
                ),
                h3: ({ children }) => (
                    <h3 className={`font-semibold text-gray-800 dark:text-white first:mt-0 ${compact ? 'text-sm mb-1 mt-2' : 'text-sm mb-2 mt-4'}`}>{children}</h3>
                ),
                p: ({ children }) => (
                    <p className={`text-gray-600 dark:text-gray-300 leading-relaxed last:mb-0 ${compact ? 'text-xs mb-2' : 'mb-3'}`}>{children}</p>
                ),
                strong: ({ children }) => <strong className="font-semibold text-gray-800 dark:text-white">{children}</strong>,
                em: ({ children }) => <em className="italic text-gray-600 dark:text-gray-300">{children}</em>,
                code: ({ className, children, ...props }) => {
                    const isBlock = className?.includes('language-');
                    if (isBlock) return <code className={`block text-xs font-mono ${className || ''}`} {...props}>{children}</code>;
                    return <code className="bg-gray-100 dark:bg-gray-700 text-primary px-1.5 py-0.5 rounded-md text-xs font-mono" {...props}>{children}</code>;
                },
                pre: ({ children }) => (
                    <pre className={`bg-gray-900 dark:bg-gray-950 text-gray-100 overflow-x-auto text-xs ${compact ? 'rounded-lg p-3 mb-2' : 'rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 mb-3'}`}>{children}</pre>
                ),
                ul: ({ children }) => <ul className={`list-disc list-inside space-y-1 text-gray-600 dark:text-gray-300 ml-1 ${compact ? 'text-xs mb-2' : 'space-y-1.5 mb-3'}`}>{children}</ul>,
                ol: ({ children }) => <ol className={`list-decimal list-inside space-y-1 text-gray-600 dark:text-gray-300 ml-1 ${compact ? 'text-xs mb-2' : 'space-y-1.5 mb-3'}`}>{children}</ol>,
                li: ({ children }) => <li className="text-gray-600 dark:text-gray-300 leading-relaxed">{children}</li>,
                table: ({ children }) => (
                    <div className={`overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 ${compact ? 'mb-2' : 'mb-3 shadow-sm'}`}>
                        <table className={`w-full border-collapse ${compact ? 'text-xs' : 'text-sm'}`}>{children}</table>
                    </div>
                ),
                thead: ({ children }) => <thead className="bg-gray-50 dark:bg-gray-800">{children}</thead>,
                th: ({ children }) => <th className={`px-4 py-2.5 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700 ${compact ? '' : 'uppercase tracking-wider'}`}>{children}</th>,
                td: ({ children }) => <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700/50">{children}</td>,
                tr: ({ children }) => <tr className="even:bg-gray-50/50 dark:even:bg-gray-800/30 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">{children}</tr>,
                blockquote: ({ children }) => (
                    <blockquote className={`border-l-4 border-primary bg-primary/5 dark:bg-primary/10 rounded-r-lg text-gray-600 dark:text-gray-300 italic ${compact ? 'py-1 px-3 mb-2 text-xs' : 'py-2 px-4 mb-3'}`}>{children}</blockquote>
                ),
                hr: () => <hr className={`border-gray-200 dark:border-gray-700 ${compact ? 'my-2' : 'my-4'}`} />,
                span: ({ children, node, ...resto }: any) => typeof resto[ATRIBUTO_UC] === 'string'
                    ? <UcCitadaBotao numero={resto[ATRIBUTO_UC]}>{children}</UcCitadaBotao>
                    : <span {...resto}>{children}</span>,
                a: ({ children, href }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">{children}</a>,
            }}
        >
            {value}
        </ReactMarkdown>
    );
};

export default TaskDescriptionMarkdown;
